// External Packages
import { useState, useEffect, useContext, useCallback, Fragment } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";

// Internal Packages
import { DataContext } from "../../context/data-context";
import { AuthContext } from "../../context/auth-context";
import { timestampToString, timestampToDate } from "../../util/timestamp-to-date";
import { toTitleCase } from "../../util/toTitleCase";
import { generateReceipt } from "../../util/generateReceipt";
import { fetchBookingById, forceAgreement } from "../../api/fetch";
import { cancelBooking, completeBooking } from "../../api/add";

// External Components
import { FaPlay, FaStop } from "react-icons/fa";
import { TiCancel } from "react-icons/ti";

// Internal Components
import LoadingSpinnerWrapper from "../UI/LoadingSpinnerWrapper";
import DetailPair from "../UI/DetailPair";
import Heading from "../UI/Heading";
import ExtendBooking from "./ExtendBooking";
import CheckInForm from "./CheckInForm";
import CheckOutForm from "./CheckOutForm";

/*
  * Booking Info Page
  This page displays all information about a booking.
*/

export default function BookingInfo() {
  // Get User from Context
  const { user } = useContext(AuthContext);
  const { fetchBookings, refreshData } = useContext(DataContext);
  const navigate = useNavigate();

  // Get Booking ID from URL
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  // Initialize States
  const [booking, setBooking] = useState();
  const [loading, setLoading] = useState(true);
  const [warning, setWarning] = useState(false);
  const [extend, setExtend] = useState(false);
  const [checkIn, setCheckIn] = useState(false);
  const [checkOut, setCheckOut] = useState(false);

  // =================== FETCH BOOKING DATA ===================
  const fetchBookingData = useCallback(() => {
    setLoading(true);
    setCheckIn(false);
    setCheckOut(false);
    fetchBookingById(user, id)
      .then((data) => {
        setBooking(data);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setWarning(err.message);
        setLoading(false);
      });
  }, [user, id]);

  // =================== COMPLETE BOOKING ===================
  const completeBookingHandler = async () => {
    try {
      setLoading(true);
      await completeBooking(user, id);
      fetchBookings();
      navigate(`/bookings/info?id=${id}`);
      setLoading(false);
    } catch (err) {
      console.log(err);
      setWarning(err.message);
      setLoading(false);
    }
  };

  // =================== CANCEL BOOKING ===================
  const cancelBookingHandler = async () => {
    try {
      setLoading(true);
      await cancelBooking(user, { bookingId: id });
      refreshData(() => navigate(`/bookings`))
      setLoading(false);
    } catch (err) {
      console.log(err);
      setWarning(err.message);
      setLoading(false);
    }
  };

  // =================== Generate Receipt Handler ===================
  const generate = () => {
    let taxes = booking.price?.taxes;
    if (!taxes || taxes === 0) {
      taxes = Number(booking.price.total) * 0.0625;
    }

    if (
      timestampToDate(booking.start_date) <
      timestampToDate(booking.date_created)
    )
      booking.date_created = booking.start_date;

    const receipt = {
      date_created: timestampToString(booking.date_created, "MM/dd/yyyy"),
      description: `Booking for ${booking.vehicle.make} ${booking.vehicle.model}`,
      subtotal: Number(booking.price.total - taxes).toFixed(2),
      taxes: Number(taxes).toFixed(2),
      amount: booking.price.total,
      status: "Paid",
      due_date: timestampToString(booking.date_created, "MM/dd/yyyy"),
      id: booking.payment.id,
      total: booking.price.total,
      booking: {
        start_date: timestampToString(booking.start_date, "MM/dd/yyyy"),
        end_date: timestampToString(booking.end_date, "MM/dd/yyyy"),
        id: booking.id,
      },
      vehicle: {
        make: booking.vehicle.make,
        model: booking.vehicle.model,
        year: booking.vehicle.year,
      },
    };
    generateReceipt(receipt, booking.customer);
  };

  const forceAgreementHandler = async () => {
    setLoading(true);
    const url = await forceAgreement(user, id);
    window.open(url.url, "_blank");
    setLoading(false);
  };

  // =================== FETCH BOOKING DATA ON LOAD ===================
  useEffect(() => {
    fetchBookingData();
  }, [fetchBookingData]);

  if (!id)
    return (
      <div className="container">
        <p className="text-stone-400">Booking ID Not Provided!</p>
      </div>
    );

  if (!loading && !booking)
    return (
      <div className="container">
        <p className="text-stone-400">Booking Not Found!</p>
      </div>
    );

  if (checkIn)
    return (
      <CheckInForm
        setOpen={setCheckIn}
        booking={booking}
        onComplete={fetchBookingData}
      />
    );
  if (checkOut)
    return (
      <CheckOutForm
        setOpen={setCheckOut}
        booking={booking}
        onComplete={fetchBookingData}
      />
    );

  const renderIcons = () => {
    if (booking?.status === "confirmed" || booking?.status === "pending")
      return (
        <Fragment>
          <TiCancel
            onClick={cancelBookingHandler}
            className="text-3xl text-white cursor-pointer hover:text-stone-400"
            title="Cancel Booking"
          />
          <FaPlay
            onClick={() => setCheckIn(true)}
            className="text-lg text-white cursor-pointer hover:text-stone-400"
            title="Start Booking"
          />
        </Fragment>
      );
    if (booking?.status === "active")
      return (
        <FaStop
          onClick={completeBookingHandler}
          className="text-lg text-white cursor-pointer hover:text-stone-400"
          title="Complete Booking"
        />
      );
  };

  return (
    <div className="container">
      <Heading title="Booking Details">{renderIcons()}</Heading>

      {loading ? (
        <LoadingSpinnerWrapper />
      ) : (
        <div className="info">
          <DetailPair label="Booking ID" value={booking.id} />
          <DetailPair
            label="Customer ID"
            value={booking.customer.id}
            link={`/customers/info?id=${booking.customer.id}`}
          />
          <DetailPair
            label="Vehicle ID"
            value={booking.vehicle.id}
            link={`/vehicles/info?id=${booking.vehicle.id}`}
          />
          <DetailPair
            label="Vehicle"
            value={
              booking.vehicle.make +
              " " +
              booking.vehicle.model +
              " " +
              booking.vehicle.year +
              " " +
              booking.vehicle.license
            }
            link={`/vehicles/info?id=${booking.vehicle.id}`}
          />
          <DetailPair
            label="Customer"
            value={booking.customer.firstName + " " + booking.customer.lastName}
            link={`/customers/info?id=${booking.customer.id}`}
          />
          <DetailPair label="Status" value={toTitleCase(booking.status)} />
          <DetailPair
            label="Date Created"
            value={timestampToString(
              booking.date_created,
              "eee dd MMM, yyyy - hh:mm a"
            )}
          />
          <DetailPair
            label="Start Date"
            value={timestampToString(
              booking.start_date,
              "eee dd MMM, yyyy - hh:mm a"
            )}
          />
          <DetailPair
            label="End Date"
            value={timestampToString(
              booking.end_date,
              "eee dd MMM, yyyy - hh:mm a"
            )}
          />
          <DetailPair
            label="Pick Up Location"
            value={booking.pick_up_location}
          />
          <DetailPair
            label="Drop Off Location"
            value={booking.drop_off_location}
          />
          <DetailPair
            label="Booking Total"
            value={"$" + booking.price.booking}
          />
          <DetailPair
            label="Delivery Fee"
            value={"$" + booking.price.delivery}
          />
          <DetailPair
            label="Total (incl. taxes)"
            value={"$" + booking.price.total}
          />
          <DetailPair
            label="Start Mileage"
            value={`${booking.start_mileage || "-"}mi`}
          />
          <DetailPair
            label="Miles Included"
            value={`${booking.miles_included || "-"}mi`}
          />
          <DetailPair
            label="Extra Mileage Rate"
            value={`$${booking.mileage_rate || "0.35"}/mi`}
          />

          <hr className="hr md:col-span-3" />

          {extend ? (
            <ExtendBooking
              booking={booking}
              setExtend={setExtend}
              refresh={fetchBookingData}
            />
          ) : (
            <Fragment>
              <div
                onClick={() =>
                  navigate(
                    `/payments/add?customerId=${booking.customer.id}&bookingId=${booking.id}`
                  )
                }
                className="btn-secondary"
              >
                Charge Customer
              </div>
              <div onClick={() => setExtend(true)} className="btn-secondary">
                Extend Booking
              </div>
              {booking.agreement ? (
                <a
                  href={booking.agreement}
                  target="_blank"
                  className="btn-secondary"
                >
                  Download Agreement
                </a>
              ) : (
                <div onClick={forceAgreementHandler} className="btn-secondary">
                  Force Agreement
                </div>
              )}
              <div onClick={generate} className="btn-secondary">
                Download Receipt
              </div>
              <div
                onClick={() =>
                  navigate(
                    `/tolls/booking?customerId=${booking.customer.id}&bookingId=${booking.id}`
                  )
                }
                className="btn-secondary"
              >
                View Tolls
              </div>
              <div
                onClick={() =>
                  navigate(
                    `/charging/booking?customerId=${booking.customer.id}&bookingId=${booking.id}`
                  )
                }
                className="btn-secondary"
              >
                View Charging
              </div>
            </Fragment>
          )}

          <div className="text-sm text-start">
            <p className="text-red-600">{warning}</p>
          </div>
        </div>
      )}
    </div>
  );
}
